import React, { useEffect, useRef, useState } from 'react';
import { isEmpty, sortBy } from 'lodash-es';
import Button from '@strava/ui/Button';
import { FOLLOWING_STATUS } from 'constants/follows/following-constants';
import { followAthlete } from 'api/athlete';
import styles from './FacebookFriendsList.scss';
import FriendEntry from './FriendEntry';
import { t } from '../../../../../../utils/invite-friends-utils';
import {
  ADD_ALERT,
  CLEAR_ALERT,
  UPDATE_FB_FRIEND_FOLLOWING_STATUS
} from '../../../../../../state/InviteFriends.actions';
import { useInviteFriendsContext } from '../../../../../../state/InviteFriendsContext';

/* display list of friends with CTA to follow them */
const FacebookFriendsList = () => {
  const { state, dispatch, track } = useInviteFriendsContext();
  const { fbFriends } = state;

  const sortedFriends = sortBy(Object.values(fbFriends), 'sortedIndex');

  const [isProcessing, setIsProcessing] = useState(false);

  // fbFriends will change as friends get followed, so we
  // want to track the initial screen_enter
  const initialScreenEnter = useRef(true);
  useEffect(() => {
    if (initialScreenEnter.current) {
      track({
        element: 'facebook_friends_list',
        action: 'screen_enter',
        properties: {
          current_tab: 'facebook',
          friends_count: sortedFriends.length
        }
      });
      initialScreenEnter.current = false;
    }
  }, [sortedFriends, track]);

  const handleFollowAll = async () => {
    setIsProcessing(true);
    dispatch({ type: CLEAR_ALERT });

    const followableFriends = sortedFriends.filter(
      (friend) => friend.isFollowable
    );

    // followSuccess lets us break the loop if a follow attempt fails
    // same follow request as for the individual FriendEntry
    // TODO hopefully replace this with bulk follow endpoint
    let followSuccess = true;
    followableFriends.every((friend) => {
      followAthlete({
        followerId: state.currentAthlete.id,
        followingId: friend.id
      }).then((status) => {
        if (status === FOLLOWING_STATUS.error) {
          dispatch({
            type: ADD_ALERT,
            payload: {
              alert: { severity: 'error', message: t(`modal.errors.generic`) }
            }
          });
          followSuccess = false;
        } else {
          dispatch({
            type: UPDATE_FB_FRIEND_FOLLOWING_STATUS,
            payload: {
              id: friend.id,
              isFollowable: false,
              followingStatus: friend.approveFollowers
                ? FOLLOWING_STATUS.pending
                : FOLLOWING_STATUS.following
            }
          });
        }
      });
      return followSuccess;
    });

    track({
      element: 'follow_all_btn',
      action: 'click',
      properties: {
        current_tab: 'facebook',
        follow_all_count: followableFriends.length,
        follow_all_status: followSuccess ? 'success' : 'error'
      }
    });

    setIsProcessing(false);
  };

  return (
    <>
      <div className={styles.fbHeader}>
        <h3 className={styles.headline}>
          {t('modal.facebook.follow_strava_fb_friends.title')}
        </h3>
        {!isEmpty(fbFriends) && (
          // follow all
          <Button
            type="button"
            variant="primary"
            disabled={
              isProcessing ||
              !sortedFriends.some((friend) => friend.isFollowable)
            }
            onClick={handleFollowAll}
          >
            {t('modal.facebook.follow_strava_fb_friends.follow_all')}
          </Button>
        )}
      </div>
      {!isEmpty(fbFriends) ? (
        <ul className={styles.friendList}>
          {sortedFriends.map((friend) => (
            <FriendEntry id={friend.id} key={friend.id} />
          ))}
        </ul>
      ) : (
        <p>
          {t('modal.facebook.follow_strava_fb_friends.no_fb_friends_on_strava')}
        </p>
      )}
    </>
  );
};

export default FacebookFriendsList;
